import { ref, computed, watch, nextTick } from 'vue';
import { addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, addYears, subYears, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns'

export function useGraphCalendarController({ calendarViewChangeCallback } = {}) {
  const selectedCalendarDate = ref(new Date());
  const currentCalendarView = ref('week');
  const startDate = ref( getStartDate({ referenceDate: selectedCalendarDate.value, currentStartDate: null, calendarView: currentCalendarView.value }) )
  const endDate = ref( getEndDate({ referenceDate: selectedCalendarDate.value, startDate: startDate.value, currentEndDate: null, calendarView: currentCalendarView.value }) )
  const dateOffset = ref(0)
  const weekOffset = ref(0)
  const monthOffset = ref(0)
  const yearOffset = ref(0)

  const calendarWeeks = computed(() => {
    const weeks = []
    let current = startDate.value
    const endOfWeekDate = endDate.value

    while (current < endOfWeekDate) {
      weeks.push({ startDate: current, endDate: endOfDay(addDays(current, 6)) })
      current = addDays(current, 7)
    }

    return weeks
  })

  function getStartDate({
    referenceDate,
    currentStartDate,
    calendarView,
    forceRecalculation = false
  }) {
    if (calendarView === 'week') {
      const newStart = startOfDay(subDays(referenceDate, 1))

      if (forceRecalculation || !currentStartDate || referenceDate < currentStartDate) {
        return newStart
      }

      return currentStartDate
    }

    const monthStart = startOfMonth(referenceDate)
    return startOfWeek(monthStart)
  }

  function getEndDate({
    referenceDate,
    startDate,
    currentEndDate,
    calendarView,
    forceRecalculation = false
  }) {
    if (calendarView === 'week') {
      const newEnd = endOfDay(addDays(startDate, 6))

      if (forceRecalculation || !currentEndDate || referenceDate > currentEndDate) {
        return newEnd
      }

      return currentEndDate
    }

    const monthEnd = endOfMonth(referenceDate)
    return endOfWeek(monthEnd)
  }

  function addDay() {
    const newDate = addDays(selectedCalendarDate.value, 1)

    if (newDate <= endDate.value) {
      updateSelectedCalendarDate(newDate)
      return
    }

    dateOffset.value += 1
    startDate.value = addDays(startDate.value, 1)
    endDate.value = addDays(endDate.value, 1)

    updateSelectedCalendarDate(newDate)
  }

  function addWeek() {
    const newDate = addWeeks(selectedCalendarDate.value, 1)

    if (newDate <= endDate.value) {
      updateSelectedCalendarDate(newDate)
      return
    }

    weekOffset.value += 1
    startDate.value = addWeeks(startDate.value, 1)
    endDate.value = addWeeks(endDate.value, 1)

    updateSelectedCalendarDate(newDate)
  }

  function addMonth() {
    const newDate = addMonths(selectedCalendarDate.value, 1)

    if (newDate <= endDate.value) {
      updateSelectedCalendarDate(newDate)
      return
    }

    monthOffset.value += 1

    if (dateOffset.value === 0 && weekOffset.value === 0) {
      startDate.value = addMonths(startDate.value, 1)
      endDate.value = addMonths(endDate.value, 1)
    } else {
      startDate.value = addDays(startDate.value, 30)
      endDate.value = addDays(endDate.value, 30)
    }

    updateSelectedCalendarDate(newDate)
  }

  function addYear() {
    const newDate = addYears(selectedCalendarDate.value, 1)

    if (newDate <= endDate.value) {
      updateSelectedCalendarDate(newDate)
      return
    }

    yearOffset.value += 1

    if (dateOffset.value === 0 && weekOffset.value === 0) {
      startDate.value = addYears(startDate.value, 1)
      endDate.value = addYears(endDate.value, 1)
    } else {
      startDate.value = addDays(startDate.value, 365)
      endDate.value = addDays(endDate.value, 365)
    }

    updateSelectedCalendarDate(newDate)
  }

  function subtractDay() {
    const newDate = subDays(selectedCalendarDate.value, 1)

    if (newDate >= startDate.value) {
      updateSelectedCalendarDate(newDate)
      return
    }

    dateOffset.value -= 1
    startDate.value = subDays(startDate.value, 1)
    endDate.value = subDays(endDate.value, 1)

    updateSelectedCalendarDate(newDate)
  }

  function subtractWeek() {
    const newDate = subWeeks(selectedCalendarDate.value, 1)

    if (newDate >= startDate.value) {
      updateSelectedCalendarDate(newDate)
      return
    }

    weekOffset.value -= 1
    startDate.value = subWeeks(startDate.value, 1)
    endDate.value = subWeeks(endDate.value, 1)

    updateSelectedCalendarDate(newDate)
  }

  function subtractMonth() {
    const newDate = subMonths(selectedCalendarDate.value, 1)

    if (newDate >= startDate.value) {
      updateSelectedCalendarDate(newDate)
      return
    }

    monthOffset.value -= 1

    if (dateOffset.value === 0 && weekOffset.value === 0) {
      startDate.value = subMonths(startDate.value, 1)
      endDate.value = subMonths(endDate.value, 1)
    } else {
      startDate.value = subDays(startDate.value, 30)
      endDate.value = subDays(endDate.value, 30)
    }

    updateSelectedCalendarDate(newDate)
  }

  function subtractYear() {
    const newDate = subYears(selectedCalendarDate.value, 1)

    if (newDate >= startDate.value) {
      updateSelectedCalendarDate(newDate)
      return
    }

    yearOffset.value -= 1

    if (dateOffset.value === 0 && weekOffset.value === 0) {
      startDate.value = subYears(startDate.value, 1)
      endDate.value = subYears(endDate.value, 1)
    } else {
      startDate.value = subDays(startDate.value, 365)
      endDate.value = subDays(endDate.value, 365)
    }

    updateSelectedCalendarDate(newDate)
  }

  function today() {
    const newDate = new Date()

    updateSelectedCalendarDate(newDate)
  }

  function updateSelectedCalendarDate(newDate) {
    selectedCalendarDate.value = newDate

    if (isWithinInterval(selectedCalendarDate.value, { start: startDate.value, end: endDate.value })) return

    updateStartAndEndDates(true)
  }

  function updateStartAndEndDates(forceRecalculation) {
    startDate.value = getStartDate({
      referenceDate: selectedCalendarDate.value,
      currentStartDate: startDate.value,
      calendarView: currentCalendarView.value,
      forceRecalculation
    })
    endDate.value = getEndDate({
      referenceDate: selectedCalendarDate.value,
      startDate: startDate.value,
      currentEndDate: endDate.value,
      calendarView: currentCalendarView.value,
      forceRecalculation
    })
  }

  watch(currentCalendarView, async () => {
    if (typeof calendarViewChangeCallback === 'function') {
      calendarViewChangeCallback(currentCalendarView.value)
    }
    
    setTimeout(() => {
      updateStartAndEndDates(true)
    }, 0)
  })

  return {
    currentCalendarView,
    calendarWeeks,
    endDate,
    selectedCalendarDate,
    startDate,
    updateSelectedCalendarDate,
    addDay,
    addWeek,
    addMonth,
    addYear,
    subtractDay,
    subtractWeek,
    subtractMonth,
    subtractYear,
    today,
  }
}
